import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, X, TrendingUp, TrendingDown, Minus, Calendar } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, ComposedChart } from 'recharts';
import { toast } from 'sonner';

const PERIODS = [
  { label: '24h', days: 1 },
  { label: '7j', days: 7 },
  { label: '15j', days: 15 },
  { label: '30j', days: 30 },
  { label: '2 mois', days: 60 },
  { label: '3 mois', days: 90 },
];

const DOW_FR = {
  Monday: 'Lun', Tuesday: 'Mar', Wednesday: 'Mer', Thursday: 'Jeu',
  Friday: 'Ven', Saturday: 'Sam', Sunday: 'Dim'
};

export default function ListingPriceHistoryModal({ listingId, listingLabel, onClose }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!listingId) return;
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('token');
        const r = await fetch(`${apiBase}/api/listings/price-history/${listingId}?days=${days}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const json = await r.json();
        if (!ignore) setData(json);
      } catch (e) {
        if (!ignore) toast.error('Erreur historique: ' + e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [listingId, days]);

  // Build chart series with concise date labels
  const chartSeries = useMemo(() => {
    if (!data?.series) return [];
    return data.series.map(p => {
      const dt = new Date(p.captured_at);
      const dayShort = p.date ? p.date.slice(5) : '';
      const hh = String(p.hour ?? dt.getUTCHours()).padStart(2, '0');
      return {
        ts: p.captured_at,
        label: `${dayShort} ${hh}h`,
        fullLabel: `${DOW_FR[p.dow] || ''} ${p.date} ${hh}h`,
        price: p.price,
        min_price: p.min_price,
        max_price: p.max_price,
        quantity: p.quantity,
      };
    });
  }, [data]);

  // Heatmap by day of week (rows) x hour buckets (cols) — avg price per cell
  const heatmap = useMemo(() => {
    if (!data?.series || data.series.length === 0) return null;
    const dows = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    // 4 hour buckets: 0-5, 6-11, 12-17, 18-23
    const buckets = [
      { label: '00-05', min: 0, max: 5 },
      { label: '06-11', min: 6, max: 11 },
      { label: '12-17', min: 12, max: 17 },
      { label: '18-23', min: 18, max: 23 },
    ];
    const cells = {};
    for (const p of data.series) {
      if (!p.dow || p.price == null) continue;
      const bIdx = buckets.findIndex(b => p.hour >= b.min && p.hour <= b.max);
      if (bIdx < 0) continue;
      const key = `${p.dow}|${bIdx}`;
      cells[key] = cells[key] || { sum: 0, n: 0 };
      cells[key].sum += p.price;
      cells[key].n += 1;
    }
    const allValues = Object.values(cells).map(c => c.sum / c.n);
    if (allValues.length === 0) return null;
    const minV = Math.min(...allValues);
    const maxV = Math.max(...allValues);
    return { dows, buckets, cells, minV, maxV };
  }, [data]);

  const heatColor = (v, minV, maxV) => {
    if (v == null) return 'bg-slate-50 text-slate-300';
    if (maxV === minV) return 'bg-emerald-100 text-emerald-800';
    const t = (v - minV) / (maxV - minV);
    if (t < 0.2) return 'bg-blue-100 text-blue-700';
    if (t < 0.4) return 'bg-cyan-100 text-cyan-800';
    if (t < 0.6) return 'bg-emerald-100 text-emerald-800';
    if (t < 0.8) return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
  };

  const stats = data?.stats;
  const changeIcon = stats?.price_change > 0 ? <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
    : stats?.price_change < 0 ? <TrendingDown className="w-3.5 h-3.5 text-red-600" />
    : <Minus className="w-3.5 h-3.5 text-slate-400" />;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}
         data-testid="listing-history-modal">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <h3 className="font-semibold text-slate-800">Historique de prix</h3>
            <span className="text-xs text-slate-500 line-clamp-1 max-w-[400px]">{listingLabel}</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="listing-history-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500 font-medium">Période :</span>
            {PERIODS.map(p => (
              <Button key={p.days} variant={days === p.days ? 'default' : 'outline'} size="sm"
                className="h-7 text-xs px-3" onClick={() => setDays(p.days)}
                data-testid={`listing-history-period-${p.days}`}>{p.label}</Button>
            ))}
          </div>

          {loading && (
            <div className="py-12 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}

          {!loading && data && chartSeries.length === 0 && (
            <div className="py-12 text-center text-sm text-slate-500">
              <Calendar className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              Aucun snapshot disponible pour cette annonce.<br />
              <span className="text-xs text-slate-400">Les prix sont capturés automatiquement toutes les 2h.</span>
            </div>
          )}

          {!loading && data && chartSeries.length > 0 && (
            <>
              {/* Stats summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div className="bg-slate-50 rounded p-2.5 border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">Snapshots</p>
                  <p className="text-lg font-bold text-slate-800">{stats.points}</p>
                </div>
                <div className="bg-emerald-50 rounded p-2.5 border border-emerald-100">
                  <p className="text-[10px] text-emerald-600 uppercase tracking-wide">Actuel</p>
                  <p className="text-lg font-bold text-emerald-700">{stats.current_price}€</p>
                </div>
                <div className="bg-blue-50 rounded p-2.5 border border-blue-100">
                  <p className="text-[10px] text-blue-600 uppercase tracking-wide">Moyen</p>
                  <p className="text-lg font-bold text-blue-700">{stats.avg_price}€</p>
                </div>
                <div className="bg-slate-50 rounded p-2.5 border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">Min / Max</p>
                  <p className="text-sm font-semibold text-slate-700">{stats.min_price}€ / {stats.max_price}€</p>
                </div>
                <div className="bg-slate-50 rounded p-2.5 border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide flex items-center gap-1">Évolution {changeIcon}</p>
                  <p className={`text-lg font-bold ${stats.price_change > 0 ? 'text-emerald-700' : stats.price_change < 0 ? 'text-red-700' : 'text-slate-600'}`}>
                    {stats.price_change > 0 ? '+' : ''}{stats.price_change}€
                  </p>
                </div>
              </div>

              {/* Price chart */}
              <div className="border border-slate-200 rounded-md p-3">
                <p className="text-xs font-medium text-slate-600 mb-2">Évolution du prix sur {days}j ({chartSeries.length} snapshots)</p>
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart data={chartSeries} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" minTickGap={30} />
                    <YAxis yAxisId="price" tick={{ fontSize: 10, fill: '#64748b' }} domain={['auto', 'auto']} />
                    <YAxis yAxisId="qty" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <Tooltip contentStyle={{ fontSize: 11, padding: '6px 8px' }}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel || ''}
                      formatter={(value, name) => {
                        if (name === 'Stock') return [value, name];
                        return [value != null ? `${value}€` : '-', name];
                      }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
                    <Area yAxisId="price" type="monotone" dataKey="max_price" name="Max BM" stroke="#94a3b8" fill="#f1f5f9" strokeWidth={1} strokeDasharray="3 3" />
                    <Line yAxisId="price" type="monotone" dataKey="price" name="Prix" stroke="#0d9488" strokeWidth={2} dot={{ r: 2 }} />
                    <Line yAxisId="price" type="monotone" dataKey="min_price" name="Min BM" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                    <Line yAxisId="qty" type="stepAfter" dataKey="quantity" name="Stock" stroke="#3b82f6" strokeWidth={1} dot={false} opacity={0.5} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Heatmap: jour de semaine x tranche horaire — pour repérer "dimanche soir" */}
              {heatmap && (
                <div className="border border-slate-200 rounded-md p-3">
                  <p className="text-xs font-medium text-slate-600 mb-2">
                    Prix moyen par jour & tranche horaire
                    <span className="text-slate-400 font-normal ml-2">(repérer les meilleurs moments de vente)</span>
                  </p>
                  <div className="overflow-x-auto">
                    <table className="text-xs w-full">
                      <thead>
                        <tr>
                          <th className="text-left text-slate-400 font-normal py-1 pr-2">Jour \ Heure</th>
                          {heatmap.buckets.map(b => (
                            <th key={b.label} className="text-center text-slate-400 font-normal px-1">{b.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {heatmap.dows.map(d => (
                          <tr key={d}>
                            <td className="py-1 pr-2 text-slate-600 font-medium">{DOW_FR[d]}</td>
                            {heatmap.buckets.map((_, bi) => {
                              const c = heatmap.cells[`${d}|${bi}`];
                              const avg = c ? c.sum / c.n : null;
                              return (
                                <td key={bi} className="px-1 py-0.5">
                                  <div className={`rounded text-center py-1 font-medium ${heatColor(avg, heatmap.minV, heatmap.maxV)}`}>
                                    {avg != null ? `${avg.toFixed(0)}€` : '—'}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
